/**
 * Transforms a raw `GET /admin/matrix` response (Staff Ops API doc, #a-matrix)
 * into the { stores, items, sectionSummary } shape ChecklistMatrixTable /
 * ChecklistStatusLegend / useChecklistStatusFilter expect. Pure functions,
 * no fetching — src/helpers/checklistApi.js does that.
 */

const SECTION_ORDER = [
  { key: "opening", label: "Opening" },
  { key: "trading", label: "Trading" },
  { key: "back_store", label: "Back Store" },
  { key: "hygiene", label: "Hygiene" },
  { key: "closing", label: "Closing" },
];

const SECTION_LABEL_BY_KEY = Object.fromEntries(SECTION_ORDER.map((s) => [s.key, s.label]));
const SECTION_RANK_BY_KEY = Object.fromEntries(SECTION_ORDER.map((s, i) => [s.key, i]));

// Matches the Figma header's own line breaks (e.g. "The Walk Thane" ->
// "The Walk" / "Thane", "Lodha Upper Thane" -> "Lodha Upper" / "Thane"):
// the last word gets its own line, everything before it shares the first.
// "Naigaon" (single word) stays on one line.
function splitStoreName(name) {
  if (!name) return [""];
  const trimmed = name.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return [trimmed];
  return [trimmed.slice(0, lastSpace), trimmed.slice(lastSpace + 1)];
}

export function adaptAdminMatrix(matrixData) {
  if (!matrixData) return { stores: [], items: [], sectionSummary: [] };

  const stores = (matrixData.stores || []).map((store) => ({
    id: store.id,
    nameLines: splitStoreName(store.name),
    managerName: store.manager?.name || "—",
    submittedToHq: Boolean(store.submission?.submittedToHq),
  }));

  // The API doesn't guarantee items arrive pre-grouped by section, and
  // ChecklistMatrixTable expects them already grouped in display order.
  const items = (matrixData.items || [])
    .map((item) => ({
      id: item.id,
      title: item.text,
      roleName: item.responsibleRole?.name || "—",
      sectionKey: item.section,
      section: SECTION_LABEL_BY_KEY[item.section] || item.section,
      // Keyed by store id, each value is the full cell object (status,
      // submissionId, response, severity, respondedBy) — not just the
      // status string — since the submission detail drawer needs
      // `submissionId` to know what to fetch when a cell is clicked.
      cellsByStore: Object.fromEntries((item.cells || []).map((cell) => [cell.storeId, cell])),
    }))
    .sort((a, b) => (SECTION_RANK_BY_KEY[a.sectionKey] ?? 99) - (SECTION_RANK_BY_KEY[b.sectionKey] ?? 99));

  const sectionSummary = SECTION_ORDER.map(({ key, label }) => ({
    name: label,
    count: items.filter((item) => item.sectionKey === key).length,
  })).filter((section) => section.count > 0);

  return { stores, items, sectionSummary };
}

// Admin summary cards (Submitted/Pending stores, Flagged/Pending/Done items)
// derived from the same { stores, items } the table renders, so the cards
// and the table can never disagree with each other.
export function getAdminMatrixSummary(stores, items) {
  const submittedStores = stores.filter((s) => s.submittedToHq).length;
  const pendingStores = stores.length - submittedStores;
  let flaggedItems = 0;
  let pendingItems = 0;
  let doneItems = 0;

  items.forEach((item) => {
    stores.forEach((store) => {
      const status = item.cellsByStore[store.id]?.status;
      if (status === "flagged") flaggedItems += 1;
      else if (status === "pending") pendingItems += 1;
      else if (status === "done") doneItems += 1;
    });
  });

  return { submittedStores, pendingStores, flaggedItems, pendingItems, doneItems };
}
